import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TreeNode } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { debounceTime, map } from 'rxjs/operators';
import { DataObject } from '../../../../../../../models/data-object.models';

/**
 * TECH DEBT (Sanjay):
 * we need to move this component to a sharable library
 *
 */

@UntilDestroy()
@Component({
  selector: 'sp-naics-input',
  templateUrl: './naics-input.component.html',
  styleUrls: ['./naics-input.component.scss'],
  standalone: false,
})
export class NaicsInputComponent implements OnInit, OnDestroy {
  @Input() optionValues: any[] = [];
  @Input() value: any;
  @Input() overlayFormControl: UntypedFormControl;

  @ViewChild('naicsOverlay', { static: true }) overlay: OverlayPanel;

  public naicsCodes: Array<{ label: string; data: string }>;
  public eventTarget: any;
  public selectedCodes = '';
  public searchBar = new UntypedFormControl();

  private hasChildren = node => !!node.children && !!node.children.length;

  ngOnInit() {
    this.naicsCodes = this.mapTreeNodes(this.optionValues);
    if (this.value && this.naicsCodes) {
      this.checkSelectedValue(this.naicsCodes);
    }
    this.searchBar.valueChanges
      .pipe(
        debounceTime(300),
        map(text => text.trim().toLowerCase())
      )
      .pipe(untilDestroyed(this))
      .subscribe(text => {
        this.naicsCodes = [...this.mapTreeNodes(this.optionValues)];
        if (text?.length >= 2) {
          this.naicsCodes = this.naicsCodes
            .map(this.expandNode.bind(this, true))
            .filter(this.filterNode.bind(this, text));
        }
      });
  }

  ngOnDestroy() {
    this.overlay = null;
  }

  filterNode = (text, node: TreeNode) => {
    if (this.hasChildren(node)) {
      node.children = node.children.filter(this.filterNode.bind(this, text));
    }
    return this.nodeIncludesText(text, node);
  };

  private anyChildrenContainsText = (text, node) =>
    node.children.some(children => this.nodeIncludesText(text, children));

  private nodeIncludesText = (text: string, node: TreeNode) =>
    node.label.toLowerCase().includes(text) ||
    (this.hasChildren(node) ? this.anyChildrenContainsText(text, node) : false);

  private expandNode = (expanded, node: TreeNode) => {
    node.expanded = expanded;
    if (this.hasChildren(node)) {
      node.children.forEach(this.expandNode.bind(this, expanded));
    }
    return node;
  };

  private mapTreeNodes(values) {
    // filtering when children null due to one of the values from the lov
    // being "(empty)", as this is not a valid value to be selected and saved
    return values
      .filter(({ value }) => value !== '(empty)')
      .map(({ key, value, children }) => {
        if (children && children.length) {
          children = this.mapTreeNodes(children);
        }
        return { label: value, data: key, children } as any as TreeNode;
      });
  }

  public toggleNode(event) {
    this.eventTarget = event.target;
    return this.overlay.toggle(event, this.eventTarget);
  }

  public nodeSelected(evt: { originalEvent?: any; node: DataObject }) {
    const { node } = evt;
    this.selectedCodes = node.label;
    this.value = node.data;
    this.overlayFormControl.setValue(this.value);
    this.overlayFormControl.markAsDirty();
    this.overlay.hide();
  }

  public clearSelectedNode(event) {
    const emptyNode = {
      node: { data: null, label: null },
    };
    event.stopPropagation();
    this.nodeSelected(emptyNode);
  }

  private checkSelectedValue(codes: DataObject[]) {
    for (const code of codes) {
      const { data, label, children = [] } = code;
      if (data === this.value) {
        this.selectedCodes = label;
        break;
      }

      if (children.length > 0) {
        this.checkSelectedValue(children);
      }
    }
  }
}
